// Bottom-anchored timelapse playback bar.
//
// Owns nothing but the UI: the parent (CourseMapPage) holds the currentIndex,
// playing, and speed state so the Mapbox layer controller and this bar share
// one source of truth. Auto-advance uses a setInterval owned by the parent
// via `onAdvance` so pause/speed-change don't double-tick.

import { useMemo } from 'react';
import { Pause, Play, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import type { TimelapseFrame } from '../api/satellite.js';
import { frameLabel } from './timelapse.js';
import { cn } from '../lib/utils';

export type TimelapseSpeed = 0.5 | 1 | 2;

interface Props {
  frames: TimelapseFrame[];
  currentIndex: number;
  playing: boolean;
  speed: TimelapseSpeed;
  onIndexChange: (i: number) => void;
  onPlayingChange: (playing: boolean) => void;
  onSpeedChange: (speed: TimelapseSpeed) => void;
  onClose: () => void;
}

const SPEEDS: TimelapseSpeed[] = [0.5, 1, 2];

export function TimelapseScrubber({
  frames,
  currentIndex,
  playing,
  speed,
  onIndexChange,
  onPlayingChange,
  onSpeedChange,
  onClose,
}: Props) {
  const current = frames[currentIndex];
  const total = frames.length;

  const label = useMemo(() => (current ? frameLabel(current.frameDate) : '—'), [current]);
  const cloud =
    current && current.cloudCover !== null ? `${Math.round(current.cloudCover)}% clouds` : null;
  const obs = current?.observationDate;

  if (total === 0 || !current) return null;

  return (
    <div
      className={cn(
        'pointer-events-auto absolute bottom-4 left-1/2 -translate-x-1/2',
        'w-[min(880px,calc(100%-2rem))] rounded-lg',
        'border border-border bg-bg-elevated/85 backdrop-blur-md',
        'shadow-2xl',
      )}
      role="region"
      aria-label="Satellite timelapse playback"
    >
      {/* Row 1 — label + meta + close */}
      <div className="flex items-center justify-between gap-3 px-4 pt-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="font-display text-xl font-semibold capitalize text-fg tracking-tight">
            {label}
          </span>
          {obs && (
            <span className="font-mono text-xs text-fg-muted truncate" title={`Scene: ${obs}`}>
              scene {obs}
            </span>
          )}
          {cloud && (
            <Badge variant="secondary" className="text-[10px] uppercase">
              {cloud}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" aria-label="Close timelapse" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Row 2 — range slider */}
      <div className="px-4 py-3">
        <input
          type="range"
          min={0}
          max={total - 1}
          step={1}
          value={currentIndex}
          onChange={(e) => onIndexChange(Number(e.currentTarget.value))}
          className={cn(
            'w-full h-2 appearance-none rounded-full cursor-pointer',
            'bg-bg-base',
            '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
            '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-greeniq-500',
            '[&::-webkit-slider-thumb]:shadow-glow-green',
            '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:bg-greeniq-500 [&::-moz-range-thumb]:border-0',
          )}
          aria-label={`Frame ${currentIndex + 1} of ${total}: ${label}`}
        />
      </div>

      {/* Row 3 — controls */}
      <div className="flex items-center justify-between gap-3 px-4 pb-3">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="icon"
            aria-label={playing ? 'Pause timelapse' : 'Play timelapse'}
            onClick={() => onPlayingChange(!playing)}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <div className="inline-flex rounded-sm border border-border overflow-hidden">
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSpeedChange(s)}
                className={cn(
                  'px-3 h-8 text-xs font-mono transition-colors',
                  s === speed
                    ? 'bg-greeniq-500 text-[#0b1014]'
                    : 'bg-transparent text-fg-muted hover:bg-bg-hover hover:text-fg',
                )}
                aria-pressed={s === speed}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
        <span className="font-mono text-xs text-fg-muted tabular-nums">
          {currentIndex + 1} / {total}
        </span>
      </div>
    </div>
  );
}
